import React, { useState, useMemo } from 'react';
import { Box, Checkbox, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Import category icons
import { ReactComponent as HomeAutoServicesIcon } from '../../../assets/icons/categories/main/HomeAutoServicesIcon.svg';
import { ReactComponent as ConstructionRenovationIcon } from '../../../assets/icons/categories/main/ConstructionRennovationIcon.svg';
import { ReactComponent as LifestyleAssistanceIcon } from '../../../assets/icons/categories/main/LifestyleAssistanceIcon.svg';
import { ReactComponent as BusinessFreelanceIcon } from '../../../assets/icons/categories/main/BusinessAndFreelanceIcon.svg';
import {
  ButtonTertiary,
  GlassTextField,
} from '../../../components/glassmorphism/GlassmorphismStyles';

const getRootCategoryIcon = (categoryKey: string) => {
  const iconStyle = { width: '48px', height: '48px' };

  switch (categoryKey.toLowerCase()) {
    case 'home_auto_services':
    case 'home_&_auto_services':
      return <HomeAutoServicesIcon style={iconStyle} />;
    case 'construction_renovation':
    case 'construction_&_renovation':
      return <ConstructionRenovationIcon style={iconStyle} />;
    case 'lifestyle_assistance':
    case 'lifestyle_&_assistance':
      return <LifestyleAssistanceIcon style={iconStyle} />;
    case 'business_freelance':
    case 'business_&_freelance':
      return <BusinessFreelanceIcon style={iconStyle} />;
    default:
      return null;
  }
};

const getLeafKeys = (node: Category): string[] => {
  const kids = node.subcategories ?? [];
  if (kids.length === 0) return [node.key];

  const res: string[] = [];
  for (const c of kids) res.push(...getLeafKeys(c));
  return res;
};

type Category = {
  key: string;
  name: string;
  subcategories?: Category[];
};

type CategoryFilterNewProps = {
  categories: Category[];
  selectedCategoryKeys: Set<string>;
  toggleCategory: (key: string) => void;
  addAllSubcategoryKeys: (keys: string[]) => void;
  removeAllSubcategoryKeys: (keys: string[]) => void;
};

const CategoryNode: React.FC<{
  node: Category;
  level: number;
  isDark: boolean;
  expanded: Set<string>;
  onToggleExpanded: (key: string) => void;
  selected: Set<string>;
  toggleCategory: (key: string) => void;
  variant: 'root' | 'group' | 'leaf';
  addCategoryKeys: (keys: string[]) => void;
  removeCategoryKeys: (keys: string[]) => void;
}> = ({
  node,
  level,
  isDark,
  expanded,
  onToggleExpanded,
  selected,
  toggleCategory,
  variant,
  addCategoryKeys,
  removeCategoryKeys,
}) => {
  const hasChildren = !!node.subcategories?.length;
  const isOpen = expanded.has(node.key);

  // leaf = without childrens(subcategories)
  const isLeaf = variant === 'leaf';

  // group = with subcutegories, but NOT root
  const isGroup = variant === 'group';

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* ROW */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: '8px',
        }}
        onClick={() => {
          if (isGroup) onToggleExpanded(node.key);
        }}
      >
        {/* LEFT: root icon, chevron or checkbox */}
        {variant === 'root' ? (
          <Box
            sx={{
              width: '48px',
              height: '48px',
              filter:
                'drop-shadow(0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4)) drop-shadow(0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3)) drop-shadow(0px 0px 34.773px rgba(0, 0, 0, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {getRootCategoryIcon(node.key)}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isLeaf && (
              <Checkbox
                checked={selected.has(node.key)}
                onChange={() => toggleCategory(node.key)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  padding: '9px',
                  '& .MuiSvgIcon-root': {
                    fontSize: 28,
                    color: isDark ? '#D5D5D5' : '#585858',
                  },
                  '&.Mui-checked .MuiSvgIcon-root': {
                    color: isDark ? '#B6B6B6' : '#393939',
                  },
                }}
              />
            )}

            {isGroup && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpanded(node.key);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  userSelect: 'none',
                  color: isDark ? '#D5D5D5' : '#1A1A1A',
                  flexShrink: 0,
                }}
              >
                {/* Chevron */}
                {isOpen ? (
                  <KeyboardArrowDownIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: isDark ? '#D5D5D5' : '#1A1A1A',
                    }}
                  />
                ) : (
                  <KeyboardArrowRightIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: isDark ? '#D5D5D5' : '#1A1A1A',
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {/* NAME */}
        <Box
          onClick={(e) => {
            if (isLeaf) {
              toggleCategory(node.key);
              e.stopPropagation();
            } else {
              e.stopPropagation();
              onToggleExpanded(node.key);
            }
          }}
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 700,
            fontSize: isLeaf ? '14px' : '16px',
            lineHeight: isLeaf ? '157%' : '175%',
            letterSpacing: isLeaf ? '0.1px' : '0.15px',
            color: isGroup
              ? isDark
                ? '#B6B6B6'
                : '#1a1a1a'
              : isDark
                ? '#D5D5D5'
                : '#393939',
            flex: 1,
            minWidth: 0,
            userSelect: 'none',
            ml: isLeaf ? '-8px' : 0,
          }}
        >
          {node.name}
        </Box>
      </Box>

      {/* CHILDREN */}
      {hasChildren && (
        <Collapse in={isOpen} timeout='auto' unmountOnExit sx={{ mb: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isGroup ? 0 : '10px',
              width: '100%',
            }}
          >
            {node.subcategories!.map((child) => (
              <CategoryNode
                key={child.key}
                node={child}
                level={level + 1}
                isDark={isDark}
                expanded={expanded}
                onToggleExpanded={onToggleExpanded}
                selected={selected}
                toggleCategory={toggleCategory}
                variant={child.subcategories?.length ? 'group' : 'leaf'}
                addCategoryKeys={addCategoryKeys}
                removeCategoryKeys={removeCategoryKeys}
              />
            ))}
          </Box>
          {isGroup && (
            <Box sx={{ display: 'flex', gap: '16px', mt: '10px' }}>
              <ButtonTertiary
                onClick={(e) => {
                  e.stopPropagation();
                  addCategoryKeys(getLeafKeys(node));
                }}
              >
                Select all
              </ButtonTertiary>
              <ButtonTertiary
                onClick={(e) => {
                  e.stopPropagation();
                  removeCategoryKeys(getLeafKeys(node));
                }}
              >
                Deselect all
              </ButtonTertiary>
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
};

const CategoryFilterNew: React.FC<CategoryFilterNewProps> = ({
  categories,
  selectedCategoryKeys,
  toggleCategory,
  addAllSubcategoryKeys,
  removeAllSubcategoryKeys,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [searchText, setSearchText] = useState('');

  const toggleExpanded = (categoryKey: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const ROOT_CATEGORY_KEYS = [
    'home_auto_services',
    'construction_renovation',
    'lifestyle_assistance',
    'business_freelance',
  ];
  const filteredRoots = useMemo(() => {
    const t = searchText.trim().toLowerCase();

    const filtered = !t
      ? categories
      : categories.filter((c) => c.name.toLowerCase().includes(t));

    return [...filtered].sort(
      (a, b) =>
        ROOT_CATEGORY_KEYS.indexOf(a.key) - ROOT_CATEGORY_KEYS.indexOf(b.key),
    );
  }, [categories, searchText]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '16px',
        gap: '24px',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
        borderRadius: '32px',
        width: '100%',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          fontFamily: 'Montserrat',
          fontWeight: 800,
          fontSize: '20px',
          lineHeight: '160%',
          letterSpacing: '0.15px',
          color: isDark ? '#D5D5D5' : '#1A1A1A',
          textShadow: '0px 1px 1px rgba(0, 0, 0, 0.15)',
        }}
      >
        Categories
      </Box>

      {/* Search Input */}
      <GlassTextField
        placeholder='Search categories'
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
        sx={{
          '& input::placeholder': {
            color: isDark ? '#D5D5D5' : '#585858',
            opacity: 1,
          },
        }}
      />

      {/* Root list */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {filteredRoots.map((root) => {
          const rootOpen = expandedCategories.has(root.key);
          const allLeafKeys = getLeafKeys(root);

          const selectedLeavesCount = allLeafKeys.filter((key) =>
            selectedCategoryKeys.has(key),
          ).length;

          const isAllSelected =
            allLeafKeys.length > 0 &&
            selectedLeavesCount === allLeafKeys.length;
          const isIndeterminate =
            selectedLeavesCount > 0 && selectedLeavesCount < allLeafKeys.length;

          const handleRootCheckboxChange = (
            e: React.ChangeEvent<HTMLInputElement>,
          ) => {
            e.stopPropagation();
            if (isAllSelected) {
              removeAllSubcategoryKeys(allLeafKeys);
            } else {
              addAllSubcategoryKeys(allLeafKeys);
            }
          };

          return (
            <Box
              key={root.key}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '8px 15px 8px 24px',
                background: isDark ? '#1A1A1A' : '#E5E5DC',
                boxShadow: `
                  0px 0px 34.77px 0px #0000000D inset, 
                  0px -0.5px 0.25px 0px #00000080 inset,
                  0px 0.5px 0.75px 0px #FFFFFF inset,
                  0px 2.07px 3.73px 0px #0000004D,
                  0px 2.08px 2.48px -2px #00000066,
                  0px 3.08px 2px -2px #00000054
                `,
                borderRadius: '32px',
                zIndex: 1,
                transition: 'all 0.3s ease',
                '& > div': { gap: '10px' },
                '&:hover': { cursor: 'pointer' },
                ...(rootOpen && {
                  boxShadow: `0px 0px 10px 0px #0000000A,
                    0px -0.5px 0.25px 0px #0000004D,
                    0px 0.5px 0.75px 0px #FFFFFF,
                    0px 2.07px 3.73px 0px #00000045 inset,
                    0px 1.08px 2.48px 0px #00000040 inset`,
                }),
                '&:has(.MuiCollapse-root) ': {
                  p: '16px 15px 16px 24px',
                },
              }}
            >
              {/* Root header row */}
              {/* Root row uses CategoryNode layout for icon+name */}
              <CategoryNode
                node={root}
                level={0}
                isDark={isDark}
                expanded={expandedCategories}
                onToggleExpanded={toggleExpanded}
                selected={selectedCategoryKeys}
                toggleCategory={toggleCategory}
                variant='root'
                addCategoryKeys={addAllSubcategoryKeys}
                removeCategoryKeys={removeAllSubcategoryKeys}
              />
              {rootOpen && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: '12px',
                    mb: '4px',
                    width: '100%',
                    gap: '0 !important',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleRootCheckboxChange}
                    sx={{
                      padding: '6px',
                      '& .MuiSvgIcon-root': {
                        fontSize: 28,
                        color: isDark ? '#B6B6B6' : '#1a1a1a',
                      },
                      '&.Mui-checked .MuiSvgIcon-root': {
                        color: isDark ? '#B6B6B6' : '#393939',
                      },
                    }}
                  />
                  <Box
                    onClick={() => {
                      if (isAllSelected) removeAllSubcategoryKeys(allLeafKeys);
                      else addAllSubcategoryKeys(allLeafKeys);
                    }}
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      userSelect: 'none',
                      cursor: 'pointer',
                      ml: '4px',
                      fontWeight: 700,
                      color: isDark ? '#B6B6B6' : '#1a1a1a',
                    }}
                  >
                    {isAllSelected
                      ? 'Deselect all subcategories'
                      : 'Select all subcategories'}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CategoryFilterNew;
